import axios from 'axios';
import { postData } from '../../services/axiosUtils';

const asListResponse = (data: any) => {
  if (Array.isArray(data)) {
    return { hasError: false, records: data, errorMessages: [] };
  }
  if (data?.records) {
    return data;
  }
  return { hasError: false, records: data ? [data] : [], errorMessages: [] };
};

const wrapEntity = (data: any) => {
  if (data?.hasError !== undefined) {
    return data;
  }
  return { hasError: false, result: data, errorMessages: [] };
};

export type TenantOnboardingPayload = {
  tenant?: {
    name?: string;
    slug?: string;
    type?: number;
    contactName?: string;
    contactEmail?: string;
    region?: string;
    defaultLanguage?: string;
    timeZone?: string;
    tenantGroupId?: string | null;
    groupDisplayName?: string;
    deepLinkSlug?: string;
  };
  subscription?: {
    licenseId?: string;
    paymentOccurrence?: string;
    startsAt?: string;
    trialDays?: number | null;
  };
  branding?: {
    productName?: string;
    loginTitle?: string;
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string | null;
    tempLogoBlobUrl?: string | null;
    /** Short-lived SAS (or object URL) for <img> — blob container is private. */
    logoPreviewUrl?: string | null;
  };
  publisher?: {
    name?: string;
    publisherTypeId?: number | null;
    enabledModules?: string[];
  };
  invitations?: Array<{
    email?: string;
    roleTemplateId?: string | null;
  }>;
  releaseProfile?: {
    name?: string;
    appTitle?: string;
    drawerFooterText?: string;
    privacyPolicyUrl?: string;
    deepLinkScheme?: string;
    deepLinkHost?: string;
    enableParking?: boolean;
    enableHtmlApplets?: boolean;
    enableAnalytics?: boolean;
    googleServicesJsonFileId?: number | null;
    googleServiceInfoPlistFileId?: number | null;
    launcherIconId?: number | null;
    splashForegroundIconId?: number | null;
    appIconId?: number | null;
    googleServicesJsonFile?: { fileName?: string; extractedAppId?: string } | null;
    googleServiceInfoPlistFile?: { fileName?: string; extractedAppId?: string } | null;
    deploymentMode?: number;
    tenantGroupId?: string | null;
    /** Aggregator: reuse an existing Aggregator release profile (numeric id). */
    existingProfileId?: number | null;
  };
};

export type TenantOnboardingDraft = {
  id: string;
  status: number;
  currentStep: number;
  payloadJson?: string;
  payload?: TenantOnboardingPayload;
  tempLogoBlobUrl?: string | null;
  logoPreviewUrl?: string | null;
  completedTenantId?: string | null;
  createdAt?: string;
  modifiedAt?: string;
};

const parseDraft = (raw: any): TenantOnboardingDraft => {
  const payloadJson = raw?.payloadJson ?? raw?.PayloadJson;
  let payload: TenantOnboardingPayload = {};
  if (raw?.payload && typeof raw.payload === 'object') {
    payload = raw.payload;
  } else if (payloadJson) {
    try {
      payload = JSON.parse(payloadJson);
    } catch {
      payload = {};
    }
  }
  return {
    id: raw?.id ?? raw?.Id,
    status: raw?.status ?? raw?.Status ?? 0,
    currentStep: raw?.currentStep ?? raw?.CurrentStep ?? 1,
    payloadJson,
    payload,
    tempLogoBlobUrl: raw?.tempLogoBlobUrl ?? raw?.TempLogoBlobUrl ?? null,
    logoPreviewUrl: raw?.logoPreviewUrl ?? raw?.LogoPreviewUrl ?? null,
    completedTenantId: raw?.completedTenantId ?? raw?.CompletedTenantId ?? null,
    createdAt: raw?.createdAt ?? raw?.CreatedAt,
    modifiedAt: raw?.modifiedAt ?? raw?.ModifiedAt,
  };
};

const tenantOnboardingService = {
  list: () =>
    axios
      .get(`/api/tenants/onboarding-drafts`)
      .then((response) => {
        const wrapped = asListResponse(response.data);
        if (!wrapped.hasError) {
          wrapped.records = (wrapped.records ?? []).map(parseDraft);
        }
        return wrapped;
      }),
  get: (id: string) =>
    axios
      .get(`/api/tenants/onboarding-drafts/${id}`)
      .then((response) => {
        const wrapped = wrapEntity(response.data);
        if (!wrapped.hasError && wrapped.result) {
          wrapped.result = parseDraft(wrapped.result);
        }
        return wrapped;
      }),
  create: (data: { currentStep?: number; payload?: TenantOnboardingPayload } = {}) =>
    postData({
      url: `/api/tenants/onboarding-drafts`,
      data: {
        currentStep: data.currentStep ?? 1,
        payloadJson: JSON.stringify(data.payload ?? {}),
      },
    }).then((response) => {
      const wrapped = wrapEntity(response);
      if (!wrapped.hasError && wrapped.result) {
        wrapped.result = parseDraft(wrapped.result);
      }
      return wrapped;
    }),
  update: (
    id: string,
    data: { currentStep?: number; payload?: TenantOnboardingPayload; tempLogoBlobUrl?: string | null }
  ) =>
    postData({
      url: `/api/tenants/onboarding-drafts/${id}`,
      data: {
        currentStep: data.currentStep,
        payloadJson: data.payload ? JSON.stringify(data.payload) : undefined,
        tempLogoBlobUrl: data.tempLogoBlobUrl,
      },
      method: 'PUT',
    }).then((response) => {
      const wrapped = wrapEntity(response);
      if (!wrapped.hasError && wrapped.result) {
        wrapped.result = parseDraft(wrapped.result);
      }
      return wrapped;
    }),
  uploadTempLogo: (id: string, data: { fileName: string; contentBase64: string }) =>
    postData({
      url: `/api/tenants/onboarding-drafts/${id}/logo`,
      data: {
        fileName: data.fileName,
        contentBase64: data.contentBase64,
      },
    })
      .then((response) => {
        const wrapped = wrapEntity(response);
        if (!wrapped.hasError && wrapped.result) {
          wrapped.result = {
            ...wrapped.result,
            tempLogoBlobUrl:
              wrapped.result.tempLogoBlobUrl ??
              wrapped.result.TempLogoBlobUrl ??
              null,
            logoPreviewUrl:
              wrapped.result.logoPreviewUrl ??
              wrapped.result.LogoPreviewUrl ??
              null,
          };
        }
        return wrapped;
      })
      .catch((error) => {
        const data = error?.response?.data;
        if (data?.hasError !== undefined) {
          return data;
        }
        throw error;
      }),
  complete: (id: string) =>
    postData({
      url: `/api/tenants/onboarding-drafts/${id}/complete`,
    }).then(wrapEntity),
  abandon: (id: string) =>
    axios
      .delete(`/api/tenants/onboarding-drafts/${id}`)
      .then((response) => wrapEntity(response.data))
      .catch((error) => {
        const data = error?.response?.data;
        if (data?.hasError !== undefined) {
          return data;
        }
        const status = error?.response?.status;
        const message =
          (Array.isArray(data?.errorMessages) && data.errorMessages.join(',')) ||
          data?.title ||
          (status === 404
            ? 'Draft nem található (vagy az API nincs frissítve).'
            : error?.message) ||
          'Elvetés sikertelen';
        return { hasError: true, errorMessages: [message], result: null };
      }),
};

export default tenantOnboardingService;
